import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import type { Plane } from "geom/planeTypes";
import type { DCEL } from "geom/polyclip/dcel";
import type { ClosedPolyLine2 } from "geom/polyLine2BrandedTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { ConstraintLine } from "./floorFramingConstraintTypes";
import type { FramingBoundaryWithMetadata, FramingConfig, FramingMetadata } from "./framingMetaDataTypes";
import type { FramingProfileDescriptor } from "./framingProfileDescriptorTypes";

export interface ConstraintPoint {
  readonly polyline: ClosedPolyLine2;
  readonly isFramingSupport: boolean; // Explicit tagging as framing support where framing ends
}

export type HeaderConfig = FramingConfig;
export type TrimmerConfig = FramingConfig;
export type TailJoistConfig = Omit<FramingConfig, "count">;

export interface FloorOpeningFramingConfigGroup {
  readonly headerConfig?: HeaderConfig;
  readonly trimmerConfig?: TrimmerConfig;
  readonly tailJoistConfig?: TailJoistConfig;
}

export interface JoistRayOrigin {
  readonly x: number;
  readonly y: number;
  readonly perpDistance: number;
  /** The type of framing member that should be generated at this origin point */
  readonly joistType: FloorFramingComponentType;
  /**
   * Indices of source openings for trimmer/tail joists.
   * A trimmer can belong to multiple openings when openings are aligned or adjacent.
   * undefined for regular floor joists or points not associated with an opening.
   * Used to prevent trimmers from being clipped by their own openings.
   */
  readonly sourceOpeningIndices?: number[];
  /**
   * Trimmer locations corresponding to each opening in sourceOpeningIndices.
   * Parallel array - trimmerLocations[i] corresponds to sourceOpeningIndices[i].
   * A trimmer can be "start" for one opening and "end" for another when openings are adjacent.
   */
  readonly trimmerLocations?: ("start" | "end")[];
  readonly trimmerIndex?: number;
}

export interface FloorFramingInput {
  readonly perimeter: FramingBoundaryWithMetadata; //primeter CCW
  readonly framingMetadata: FramingMetadata;
  readonly openings?: FramingBoundaryWithMetadata[]; //openings CW
  readonly openingFramingConfigGroup?: FloorOpeningFramingConfigGroup;
  readonly constraintLines?: ConstraintLine[];
  readonly constraintPoints?: ConstraintPoint[];
}

export enum MemberConnectionType {
  SUPPORT = "SUPPORT",
  FRAMING = "FRAMING",
}

export interface MemberConnectionPoint {
  readonly connectionType: MemberConnectionType;
  readonly connectedMemberId: string;
  readonly connectedMemberType: FloorFramingComponentType;
  readonly distanceFromStart: number;
}

export interface FramingMemberStructuralAnalysisData {
  readonly memberId: string;
  readonly framingPlane: Plane;
  readonly connectionPoints: MemberConnectionPoint[];
  readonly leftTributaryPolygons: ClosedPolyLine2[];
  readonly rightTributaryPolygons: ClosedPolyLine2[];
}

export interface FloorFramingMeta {
  readonly structuralData?: FramingMemberStructuralAnalysisData;
}

export interface FloorFramingBase<T extends FloorFramingComponentType> {
  readonly id: string;
  readonly profileDescriptor: FramingProfileDescriptor;
  readonly rails: LumberRail[];
  readonly type: T;
  readonly meta?: FloorFramingMeta;
}

export type FloorJoist = FloorFramingBase<FloorFramingComponentType.FLOOR_JOIST>;
export type FloorBeam = FloorFramingBase<FloorFramingComponentType.FLOOR_BEAM>;
export type RimJoist = FloorFramingBase<FloorFramingComponentType.RIM_JOIST>;
export type Header = FloorFramingBase<FloorFramingComponentType.HEADER>;
export type Trimmer = FloorFramingBase<FloorFramingComponentType.TRIMMER>;
export type TailJoist = FloorFramingBase<FloorFramingComponentType.TAIL_JOIST>;
export type Noggin = FloorFramingBase<FloorFramingComponentType.NOGGIN>;

export type Framing = FloorJoist | FloorBeam | RimJoist | Header | Trimmer | TailJoist | Noggin;

/**
 * The following will replace the GeneratedFramedFloorAssembly type, which is currently being
 * used in the building generation package but that data structure is behind a feature flag.
 */
export interface GeneratedFramedFloorAssembly extends BuildingEntityBase<"FRAMED_FLOOR_ASSEMBLY"> {
  readonly floorId: string;
  readonly levelId: string;
  readonly framing: Framing[];
}

/**
 * Test case for floor framing debug page
 */
export interface FloorFramingTestCase {
  readonly name: string;
  readonly description?: string;
  readonly input: FloorFramingInput;
}

/**
 * Internal interface for generated joist data, exported for use in structural utilities
 */
export interface GeneratedJoist {
  readonly polyline: PolyLine2;
  readonly memberId: string;
  readonly profileDescriptor: FramingProfileDescriptor;
  readonly joistType: FloorFramingComponentType;
  /** Perimeter edge index for rim joists; undefined for non-rim members. */
  readonly rimEdgeIndex?: number;
  /** Stack layer index (0 = joist-abutment face) for rim joists; undefined for non-rim members. */
  readonly rimStackIndex?: number;
  /**
   * Same-layer rim on the previous perimeter edge (mitered to this rail's start).
   * Undefined when that edge has no ply at this stack index (disabled / split / shorter stack).
   */
  readonly rimStartNeighborMemberId?: string;
  /**
   * Same-layer rim on the next perimeter edge (mitered to this rail's end).
   * Undefined when that edge has no ply at this stack index.
   */
  readonly rimEndNeighborMemberId?: string;
}

/**
 * Result from building a joist planar graph via DCEL
 */
export interface JoistPlanarGraphResult {
  readonly dcel: DCEL;
  readonly memberIdToSegmentIndex: Map<string, number>;
  readonly segmentIndexToMemberId: Map<number, string>;
  readonly joistTypeByMemberId: Map<string, FloorFramingComponentType>;
}
